import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { format } from "date-fns";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PageLoader from "../../../../common/ui/PageLoader";
import {
  getHistoryInbody,
  getHistoryInbodyRecord,
} from "../../../../common/network/Api";
import { getApiErrorMessage } from "../../../../common/utils/apiError";
import { formatDisplayDate } from "../../../../common/utils/date";
import { enBiomarkerCategory } from "../../store/helper";
import { getStatusBadgeStyle } from "../../utils/labPanelStatus";
import { INBODY_STATUS_LABEL } from "./assessmentHistory";
import {
  HistoryInbodyBiomarker,
  HistoryInbodyListItem,
} from "../../entities/history";
import ScrollableWrapper from "../common/ScrollableWrapper";
import ClientBadge from "../common/ClientBadge";
import HistoryNotReadyState from "./HistoryNotReadyState";
import useHistoryScope from "./useHistoryScope";
import useHistoryClientName from "./useHistoryClientName";
import { HISTORY_FETCH_PAGE_SIZE } from "./useHistoryList";

/**
 * `…/assessments/inbody/:recordId` — one InBody test, full page (design node
 * 16930:76910).
 *
 * Two requests, because the backend splits the record: `GET
 * /history/inbody/{id}` returns the batch's biomarkers — value, unit,
 * reference range, test date, the client's id — and nothing about the record
 * itself, while the record (device, import date, test date) lives only in the
 * `/history/inbody` listing. Coming from the table, the row arrives in router
 * state; on a cold load the page looks the record up in that client's
 * listing, and if that misses the header falls back to what the biomarkers
 * carry and leaves out the device.
 *
 * The header badge reads "Completed · Approved" for every test, as the design
 * and the listing do — an imported batch is by definition a finished,
 * accepted result. The table's per-biomarker Status column has no source
 * (InBody sends no normal/borderline flags), so it is left out rather than
 * filled with "No status available".
 *
 * The way back to the tab is the breadcrumb's (`useBackNavigation`).
 */

const InbodyTestDetailRoute = () => {
  const theme: Theme = useTheme();
  const { recordId } = useParams();
  const location = useLocation();
  const { kind } = useHistoryScope();

  const [biomarkers, setBiomarkers] = useState<HistoryInbodyBiomarker[] | null>(
    null
  );
  const [record, setRecord] = useState<HistoryInbodyListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The listing row, when the user came from the table (`AssessmentsHistoryTab`
  // passes it in router state). Absent on a cold load.
  const stateRecord =
    (location.state as { record?: HistoryInbodyListItem } | null)?.record ??
    null;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      setBiomarkers(null);
      setRecord(null);
      if (!recordId) {
        setLoading(false);
        return;
      }
      try {
        const response = await getHistoryInbodyRecord(recordId);
        if (cancelled) return;
        const items = Array.isArray(response) ? response : [];
        setBiomarkers(items);

        if (stateRecord && stateRecord.id === recordId) {
          setRecord(stateRecord);
        } else if (items[0]?.customerid) {
          // Cold load: the record's own fields (device, dates) live only in
          // the listing, so look it up there. A miss just hides the device.
          try {
            const page = await getHistoryInbody({
              page: 1,
              rows: HISTORY_FETCH_PAGE_SIZE,
              userid: items[0].customerid,
            });
            if (cancelled) return;
            setRecord(page?.data?.find((item) => item.id === recordId) ?? null);
          } catch (e) {
            console.warn("Couldn't load the InBody record's details:", e);
          }
        }
      } catch (e: any) {
        if (cancelled) return;
        console.error("Error loading InBody test:", e);
        if (e?.response?.status === 404) {
          setBiomarkers([]);
        } else {
          setError(getApiErrorMessage(e, "Couldn't load this InBody test."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [recordId, stateRecord]);

  const customerID = biomarkers?.[0]?.customerid ?? null;
  const { name: clientName, resolving: clientNameResolving } =
    useHistoryClientName(customerID, kind === "company");

  const measurementDate =
    record?.testDate ??
    biomarkers?.find((b) => b.datecreated)?.datecreated ??
    null;
  // The title is when the test entered the platform; the measurement date is
  // its own field below, as in the design.
  const titleDate = record?.dateCreated ?? measurementDate;

  const groups = useMemo(() => {
    const byCategory = new Map<number, HistoryInbodyBiomarker[]>();
    (biomarkers ?? []).forEach((biomarker) => {
      const list = byCategory.get(biomarker.category) ?? [];
      list.push(biomarker);
      byCategory.set(biomarker.category, list);
    });
    return Array.from(byCategory.entries()).sort(([a], [b]) => a - b);
  }, [biomarkers]);

  // The record lookup is chained into `loading`; the badge's name resolves
  // separately, so wait for it too and render the header in one go.
  if (loading || clientNameResolving) return <PageLoader />;

  if (error) {
    return <HistoryNotReadyState title="Couldn't load this test" text={error} />;
  }

  if (!biomarkers || biomarkers.length === 0) {
    return (
      <HistoryNotReadyState
        title="Test not found"
        text="This InBody test is no longer available. Go back to Assessments to see the current list."
      />
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.contentWrapper}>
        <ScrollableWrapper>
          <Box sx={styles.card(theme)}>
            {/* Header: date, client badge, then the record's facts */}
            <Box sx={styles.header}>
              <Box sx={styles.titleRow}>
                <Box sx={styles.titleGroup}>
                  <Box sx={styles.title(theme)}>
                    {titleDate ? formatDisplayDate(titleDate) : "InBody test"}
                  </Box>
                  <Box sx={getStatusBadgeStyle("success", theme)}>
                    {INBODY_STATUS_LABEL}
                  </Box>
                </Box>
                {/* Only the cross-client page needs the badge; the workspace
                    shells already name the subject in their top bar. */}
                {kind === "company" && <ClientBadge name={clientName} />}
              </Box>
              <Box sx={styles.facts}>
                <Fact label="Provider" value="InBody" />
                <Fact label="Test type" value="InBody test" />
                <Fact
                  label="Date of measurement"
                  value={
                    measurementDate ? formatDisplayDate(measurementDate) : "—"
                  }
                />
              </Box>
              {record?.device && (
                <Box sx={styles.facts}>
                  <Fact label="Device" value={`InBody ${record.device}`} />
                </Box>
              )}
            </Box>

            {/* Biomarkers, one table per category */}
            <Box sx={styles.biomarkers}>
              <Box sx={styles.sectionLabel(theme)}>
                {`Biomarkers (${biomarkers.length})`}
              </Box>
              {groups.map(([category, items]) => (
                <Box key={category} sx={styles.group}>
                  <Box sx={styles.groupTitle(theme)}>
                    {categoryTitle(category)}
                  </Box>
                  <Box sx={styles.table(theme)}>
                    <Box sx={styles.tableHead(theme)}>
                      <Box sx={{ ...styles.headCell(theme), ...styles.colName }}>
                        Name
                      </Box>
                      <Box sx={{ ...styles.headCell(theme), ...styles.colValue }}>
                        Value
                      </Box>
                      <Box sx={{ ...styles.headCell(theme), ...styles.colRange }}>
                        Reference Range
                      </Box>
                      <Box sx={{ ...styles.headCell(theme), ...styles.colDate }}>
                        Date
                      </Box>
                    </Box>
                    <Box sx={styles.tableBody}>
                      {items.map((item, index) => (
                        <Box
                          key={`${item.key}-${index}`}
                          sx={{
                            ...styles.row(theme),
                            ...(index === items.length - 1
                              ? { borderBottom: "none" }
                              : {}),
                          }}
                        >
                          <Box sx={{ ...styles.name(theme), ...styles.colName }}>
                            {item.name}
                          </Box>
                          <Box sx={{ ...styles.value(theme), ...styles.colValue }}>
                            <ValueWithUnit item={item} />
                          </Box>
                          <Box
                            sx={{
                              ...styles.range(theme, !item.label),
                              ...styles.colRange,
                            }}
                          >
                            {item.label || "—"}
                          </Box>
                          <Box sx={{ ...styles.date(theme), ...styles.colDate }}>
                            {formatRowDate(item.datecreated)}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </ScrollableWrapper>
      </Box>
    </Box>
  );
};

export default InbodyTestDetailRoute;

/** Label over value — the header's "Provider / InBody" pairs. */
const Fact = ({ label, value }: { label: string; value: string }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.fact}>
      <Box sx={styles.factLabel(theme)}>{label}</Box>
      <Box sx={styles.factValue(theme)}>{value}</Box>
    </Box>
  );
};

/** "33.1 lb" — bold number, medium unit, as the design sets them. */
const ValueWithUnit = ({ item }: { item: HistoryInbodyBiomarker }) => {
  const theme: Theme = useTheme();
  if (item.value === null || item.value === undefined) return <>—</>;
  const number = Number(item.value);
  const text = isNaN(number)
    ? String(item.value)
    : Number.isInteger(number)
    ? String(number)
    : String(parseFloat(number.toFixed(2)));
  return (
    <>
      <Box component="span" sx={styles.valueNumber(theme)}>
        {text}
      </Box>
      {item.unit ? ` ${item.unit}` : ""}
    </>
  );
};

/**
 * The group heading. The shared category enum names them "… Markers"
 * ("Body Composition Markers"); on this page every row is a marker, so the
 * design drops the word.
 */
const categoryTitle = (category: number): string =>
  (enBiomarkerCategory[category] ?? `Category ${category}`).replace(
    /\s+markers$/i,
    ""
  );

/** "08 May 2025" — the table's date format in the design. */
const formatRowDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy");
};

const styles = {
  root: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    boxSizing: "border-box" as "border-box",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden" as "hidden",
    marginRight: "-10px",
  },
  card: (theme: Theme) => ({
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  header: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
    minHeight: "28px",
  },
  titleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    minHeight: "28px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  facts: {
    display: "flex",
    gap: "16px",
  },
  fact: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  factLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  factValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  biomarkers: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  group: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
    marginBottom: "16px",
  },
  groupTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  table: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden" as "hidden",
  }),
  tableHead: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "32px",
    padding: "4px 28px",
    // Figma #F2F4F7; the nearest theme neutral, agreed with the user.
    backgroundColor: theme.colorTokens.N[30],
  }),
  headCell: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "18px",
    letterSpacing: "1px",
    textTransform: "capitalize" as "capitalize",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    padding: "2px 0",
  }),
  tableBody: {
    display: "flex",
    flexDirection: "column" as "column",
    padding: "0 12px",
  },
  row: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "32px",
    minHeight: "52px",
    padding: "8px 16px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  // Column widths, shared by the header and the rows.
  colName: { flex: 2.6, minWidth: 0 },
  colValue: { flex: 0.9, minWidth: 0 },
  colRange: { flex: 2.4, minWidth: 0 },
  colDate: { width: "132px", flexShrink: 0 },
  name: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  value: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
  }),
  valueNumber: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  range: (theme: Theme, empty: boolean) => ({
    fontFamily: empty ? "var(--font-Medium)" : "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: empty ? 1.6 : 1.4,
    color: empty
      ? theme.colors_ds.NEUTRAL_TEXT_INACTIVE
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  date: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.4,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
